import { ChevronLeft, ChevronRight, RotateCcw, Share2, Sparkles, Upload } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

import { Badge } from '@/components/common/Badge/Badge';
import { Button } from '@/components/common/Button/Button';
import { Tip } from '@/components/common/Tip/Tip';
import { classNames } from '@/utils/classNames';

import styles from './PoliticalMatchResultHero.module.css';

type PoliticalMatchResultHeroProps = {
  title: string;
  description: string[];
  titleNote?: {
    message: string;
    tip: string;
  };
  candidateName: string;
  candidatePhotoUrl?: string | null;
  showCandidatePhoto?: boolean;
  userPhotoUrl?: string | null;
  userPhotoAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  candidateRankLabel?: string;
  candidateNavigation?: {
    onPrevious: () => void;
    onNext: () => void;
    previousDisabled?: boolean;
    nextDisabled?: boolean;
    previousLabel?: string;
    nextLabel?: string;
  };
  resultActions?: {
    share: {
      label: string;
      to: string;
      disabled?: boolean;
    };
    restart: {
      label: string;
      onClick: () => void;
      disabled?: boolean;
    };
  };
  className?: string;
};

type ImageWithFallbackProps = {
  src: string | null;
  alt: string;
  wrapperClassName: string;
  imageClassName: string;
  fallback: ReactNode;
};

const STORY_HIGHLIGHT_PHRASES = [
  'mudança',
  'inclusão',
  'novos caminhos',
  'liberdade econômica',
  'liberdade pessoal',
  'liberdade de escolha',
  'menos interferência',
  'proteção social',
  'regras claras',
  'autonomia',
  'iniciativa',
  'independência',
  'flexibilidade',
  'cooperação',
  'eficiência',
  'resultados',
  'equilíbrio',
  'diálogo',
  'viabilidade',
  'síntese',
  'convívio',
  'ação pública',
  'ordem',
  'estabilidade',
  'previsibilidade',
  'instituições',
  'ação coletiva',
  'tradição',
  'responsabilidade',
  'preservar',
  'vida em comum',
  'caminho próprio',
];

const STORY_HIGHLIGHT_LOOKUP = new Set(STORY_HIGHLIGHT_PHRASES.map((phrase) => phrase.toLowerCase()));
const STORY_HIGHLIGHT_PATTERN = new RegExp(`(${STORY_HIGHLIGHT_PHRASES.map(escapeRegExp).join('|')})`, 'gi');
const STORY_BOLD_PATTERN = /\*\*(.+?)\*\*/g;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
}

function renderHighlightedText(text: string, keyPrefix = ''): ReactNode[] {
  const parts = text.split(STORY_HIGHLIGHT_PATTERN).filter(Boolean);

  return parts.map((part, index) => {
    const isHighlighted = STORY_HIGHLIGHT_LOOKUP.has(part.toLowerCase());
    const key = `${keyPrefix}${index}`;

    return isHighlighted ? (
      <span key={`${key}-accent`} className={styles.resultHeroLeadAccent}>
        {part}
      </span>
    ) : (
      <span key={`${key}-plain`}>{part}</span>
    );
  });
}

function renderStoryParagraph(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  STORY_BOLD_PATTERN.lastIndex = 0;

  while ((match = STORY_BOLD_PATTERN.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(...renderHighlightedText(text.slice(lastIndex, match.index), `${keyPrefix}-plain-${nodes.length}-`));
    }

    nodes.push(
      <strong key={`${keyPrefix}-strong-${match.index}`} className={styles.resultHeroLeadStrong}>
        {renderHighlightedText(match[1] ?? '', `${keyPrefix}-bold-${match.index}-`)}
      </strong>,
    );

    lastIndex = STORY_BOLD_PATTERN.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(...renderHighlightedText(text.slice(lastIndex), `${keyPrefix}-tail-${nodes.length}-`));
  }

  return nodes;
}

function ImageWithFallback({ src, alt, wrapperClassName, imageClassName, fallback }: ImageWithFallbackProps): JSX.Element {
  const [hasError, setHasError] = useState(false);
  const normalizedSource = src?.trim() ?? '';
  const showImage = normalizedSource.length > 0 && !hasError;

  useEffect(() => {
    setHasError(false);
  }, [normalizedSource]);

  return (
    <div className={wrapperClassName}>
      {showImage ? (
        <img
          className={imageClassName}
          src={normalizedSource}
          alt={alt}
          loading="eager"
          decoding="async"
          onError={() => setHasError(true)}
        />
      ) : (
        fallback
      )}
    </div>
  );
}

function PhotoFallback({ mark }: { mark: string }): JSX.Element {
  return (
    <div className={styles.resultHeroPhotoFallback}>
      <div className={styles.resultHeroPhotoFallbackGlow} />
      <div className={styles.resultHeroPhotoFallbackMark}>{mark}</div>
    </div>
  );
}

function ResultPhotoCard({
  src,
  alt,
  tag,
  fallbackMark,
  tone = 'user',
  footer,
}: {
  src: string | null;
  alt: string;
  tag: string;
  fallbackMark: string;
  tone?: 'user' | 'candidate';
  footer?: ReactNode;
}): JSX.Element {
  return (
    <div className={styles.resultHeroPhotoCardStack}>
      <div className={classNames(styles.resultHeroPhotoCard, tone === 'candidate' && styles.resultHeroPhotoCardCandidate)}>
        <ImageWithFallback
          src={src}
          alt={alt}
          wrapperClassName={styles.resultHeroPhotoMedia!}
          imageClassName={styles.resultHeroPhotoImage!}
          fallback={<PhotoFallback mark={fallbackMark} />}
        />

        <div className={styles.resultHeroPhotoOverlay} />
        <div className={styles.resultHeroPhotoFrame} />
        <div className={styles.resultHeroPhotoTag}>{tag}</div>
      </div>

      {footer ? <div className={styles.resultHeroPhotoCardFooter}>{footer}</div> : null}
    </div>
  );
}

export function PoliticalMatchResultHero({
  title,
  description,
  titleNote,
  candidateName,
  candidatePhotoUrl,
  showCandidatePhoto = true,
  userPhotoUrl,
  userPhotoAction,
  candidateRankLabel,
  candidateNavigation,
  resultActions,
  className,
}: PoliticalMatchResultHeroProps): JSX.Element {
  const heroSource = userPhotoUrl?.trim() ? userPhotoUrl.trim() : null;
  const candidateSource = candidatePhotoUrl?.trim() ? candidatePhotoUrl.trim() : null;
  const userFooter = userPhotoAction ? (
    <div className={styles.resultHeroPhotoCardFooterInner}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className={styles.resultHeroPhotoCardUploadButton}
        fullWidth
        ariaLabel={userPhotoAction.label}
        onClick={userPhotoAction.onClick}
        disabled={userPhotoAction.disabled}
      >
        <Upload size={16} />
        {userPhotoAction.label}
      </Button>
    </div>
  ) : null;
  const candidateFooter = showCandidatePhoto && (candidateRankLabel || candidateNavigation) ? (
    <div className={styles.resultHeroPhotoCardFooterInner}>
      {candidateRankLabel ? <Badge tone="neutral" className={styles.resultHeroPhotoCardRankBadge}>{candidateRankLabel}</Badge> : null}

      {candidateNavigation ? (
        <div className={styles.resultHeroPhotoCardNav} aria-label="Navegar entre os 3 candidatos mais próximos">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={styles.resultHeroPhotoCardNavButton}
            ariaLabel={candidateNavigation.previousLabel ?? 'Selecionar candidato anterior'}
            onClick={candidateNavigation.onPrevious}
            disabled={candidateNavigation.previousDisabled}
          >
            <ChevronLeft size={16} />
          </Button>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={styles.resultHeroPhotoCardNavButton}
            ariaLabel={candidateNavigation.nextLabel ?? 'Selecionar próximo candidato'}
            onClick={candidateNavigation.onNext}
            disabled={candidateNavigation.nextDisabled}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      ) : null}
    </div>
  ) : null;
  const stickyResultActions = resultActions ? (
    <div className={styles.resultHeroActions}>
      <Button
        to={resultActions.share.to}
        variant="primary"
        className={styles.resultHeroActionButton}
        fullWidth
        disabled={resultActions.share.disabled}
      >
        <Share2 size={14} />
        {resultActions.share.label}
      </Button>

      <Button
        type="button"
        variant="secondary"
        className={styles.resultHeroActionButton}
        fullWidth
        onClick={resultActions.restart.onClick}
        disabled={resultActions.restart.disabled}
      >
        <RotateCcw size={14} />
        {resultActions.restart.label}
      </Button>
    </div>
  ) : null;

  return (
    <section className={classNames(styles.resultHero, className)} aria-label="Seu match político">
      <div className={styles.resultHeroBackdropV} aria-hidden="true">
        V
      </div>

      <div className={styles.resultHeroInner}>
        <div className={styles.resultHeroContent}>
          <div className={styles.resultHeroIntro}>
            <p className={styles.resultHeroBadge}>
              <Sparkles size={16} strokeWidth={2.4} />
              <span>SEU MATCH POLÍTICO</span>
            </p>

            <h1 className={styles.resultHeroTitle}>{title}</h1>

            {titleNote ? (
              <div className={styles.resultHeroTitleNote} role="note" aria-label="Observação sobre o resultado">
                <p className={styles.resultHeroTitleNoteMessage}>{titleNote.message}</p>
                <Tip label="Saiba mais">{titleNote.tip}</Tip>
              </div>
            ) : null}

          </div>

          <div className={styles.resultHeroPhotoRow}>
            <ResultPhotoCard src={heroSource} alt="Sua foto" tag="Você" fallbackMark="VC" footer={userFooter} />
            {showCandidatePhoto ? (
              <ResultPhotoCard src={candidateSource} alt={`Foto de ${candidateName}`} tag={candidateName} fallbackMark={getInitials(candidateName) || 'PM'} tone="candidate" footer={candidateFooter} />
            ) : null}
          </div>

          <div className={styles.resultHeroLead}>
            {description.map((paragraph, index) => (
              <p key={`${title}-${index}`} className={styles.resultHeroLeadParagraph}>
                {renderStoryParagraph(paragraph, `${title}-${index}`)}
              </p>
            ))}
          </div>
          {stickyResultActions}

        </div>
      </div>
    </section>
  );
}
